import type {RouteLocationNormalizedGeneric, RouteRecordNameGeneric} from 'vue-router';
import {START_LOCATION} from 'vue-router';
import {consumeReturnRoute} from '@/composables/useReturnRoute/useReturnRoute';

/**
 * Redirects the user to the route saved before an external redirect (e.g. the payment provider).
 *
 * Only acts on the initial navigation of a fresh page load (`from === START_LOCATION`). Returning
 * from the provider is always a full reload (different origin), so that first navigation is the
 * re-entry — wherever the provider lands the user, which we cannot know in advance.
 *
 * It must NOT act on in-session navigations: `setReturnRoute` is called right before
 * `window.location.assign`, whose unload is deferred, so code after it keeps running (e.g. the
 * `router.push` to orders-overview in the create/edit flows). Consuming on that navigation would
 * delete the key before the page actually unloads, losing it before the user ever returns.
 */
export const returnRouteGuard = (
  to: {
    name: RouteRecordNameGeneric;
    params?: Record<string, unknown>;
    query?: Record<string, unknown>;
  },
  from: RouteLocationNormalizedGeneric,
  hasRoute: (name: NonNullable<RouteRecordNameGeneric>) => boolean,
) => {
  if (from !== START_LOCATION) {
    return true;
  }

  const returnRoute = consumeReturnRoute();

  // No saved route, or we already landed on it — let the navigation proceed.
  if (!returnRoute || to.name === returnRoute.name) {
    return true;
  }

  // The stored route no longer exists (renamed/removed since it was saved). It's already consumed,
  // so it can't replay — fall through to the navigation in progress rather than pushing to a route
  // that would fail to resolve.
  if (!hasRoute(returnRoute.name)) {
    return true;
  }

  return returnRoute;
};
